import { HostAriaLabelMixin, SchmancyElement } from '@schmancy/mixins/index'
import { css, html } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import '../list/list-item'

@customElement('schmancy-menu-item')
export default class SchmancyMenuItem extends HostAriaLabelMixin(SchmancyElement) {
	static styles = [
		css`
			:host {
				display: block;
			}
		`,
	]

	/**
	 * Marks the item as the choice currently in effect. It lands on the row as
	 * `aria-current`, not `aria-selected`: `aria-selected` is not an allowed
	 * attribute on `role="menuitem"` (measured — axe `aria-allowed-attr` flags
	 * it), while `aria-current` is the state a menu's already-active entry
	 * carries.
	 */
	@property({ type: Boolean, reflect: true }) selected = false

	/**
	 * The action exists but cannot be taken right now. The item stays in the
	 * menu, in the a11y tree, and in the arrow-key order — a menu that drops the
	 * entry instead leaves the user with no way to learn the action is there at
	 * all, and one that renders it inert with opacity alone leaves a keyboard or
	 * screen-reader user with nothing to find. Pair it with `reason`.
	 */
	@property({ type: Boolean, reflect: true }) disabled = false

	/** Why the action is unavailable — the second line, and the item's accessible description. */
	@property({ type: String }) reason = ''

	private dismissParentMenu = (e: Event): void => {
		// The row inside swallows every real click while disabled, so this only
		// catches a click dispatched at this host directly — an imperative
		// `.click()`, a synthetic activation — which must not dismiss the menu
		// either, since nothing was chosen.
		if (this.disabled) return

		// The path is read HERE, during dispatch, for two reasons: an event's
		// composed path is emptied once dispatch finishes, so a microtask read
		// returns nothing; and a consumer click handler that removes this item
		// from the DOM (navigation, a list re-render) detaches it before the
		// microtask runs, and an event dispatched from a detached node reaches
		// no listener — the menu would stay open forever.
		const path = e.composedPath().filter((node): node is Node => node instanceof Node)

		// Reading defaultPrevented on a microtask lets a consumer's own click
		// listener on this same tag call preventDefault() to opt out of dismiss,
		// regardless of listener registration order.
		queueMicrotask(() => {
			if (e.defaultPrevented) return
			// Bubbling 'close' is what the enclosing menu's show() subscription watches to tear down.
			path.find(node => node.isConnected)?.dispatchEvent(new CustomEvent('close', { bubbles: true, composed: true }))
		})
	}

	connectedCallback(): void {
		super.connectedCallback()
		this.addEventListener('click', this.dismissParentMenu)
		// The item is a wrapper around the row that carries the menuitem role;
		// left generic it sits between the enclosing role="menu" and its own
		// required menuitem child, so the menu owns no menuitem of its own.
		if (!this.hasAttribute('role')) this.setAttribute('role', 'none')
	}

	disconnectedCallback(): void {
		this.removeEventListener('click', this.dismissParentMenu)
		super.disconnectedCallback()
	}

	protected render(): unknown {
		return html`
			<schmancy-list-item
				role="none"
				row-role="menuitem"
				.ariaLabel=${this.ariaLabel}
				?selected=${this.selected}
				?disabled=${this.disabled}
				.reason=${this.reason}
			>
				<slot slot="leading" name="leading"></slot>
				<slot></slot>
				<slot slot="trailing" name="trailing"></slot>
			</schmancy-list-item>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-menu-item': SchmancyMenuItem
	}
}
